// The proof: the wall of clip names, the real Premiere project, the case film,
// and the two finished films.
import { useEffect, useRef, useState } from 'react';
import { CRWIcon, type IconName } from '../components/icons';
import { LoopVideo, PrTile, SectionHead } from '../components/primitives';
import { useIsMobile, useSettled } from '../components/hooks';
import { clipsBefore, drawTimeline, useTimeline, type Group } from '../components/timeline';
import { CASE_FILM, FILMS } from '../lib/site';

/* ---------- THE WALL: the problem interstitial ---------- */
// Real clip names from the sequence of our first film. Sits between the hero
// and the first showcase; the sections after it are the answer. Reveal
// choreography is CSS-only, keyed off the section-level .crw-in class.
const WALL: string[][] = [
  ['P4W3-107_P29a_basf-e31-67s.mp4', 'SFX_117', 'W8_084', 'RIBBON R07_2025-1975_50y', 'POL_R013', 'GFXR1_G2-26_in', 'BR_1778'],
  ['P4W7-04_A22c_ceti-robot-dog.mp4', 'S2 TV512_96_CH518_38', 'SFX_144', 'W7P3_039', 'FREEZE letta in', 'P3G-03_IN', 'S3 RACE_R3'],
  ['RS P3 MUS - Pressure Drop', 'GFXR1_G1-02_in', 'BR_0824', 'POL_R048', 'SFX_067', 'RIBBON R06_1957-1970s', 'W9_010'],
  ['P3R1-15_E39f_wood-ants-close.mp4', 'JI AUDIO P4-O p2', 'SFX_022', 'H3-06 [tv-frame]', 'W7_066', 'europe voice part 1 new rx.wav', 'POL_R096'],
  ['P4W4-56_R06_craftsman-at-work.mp4', 'S3b LT_G1-05_out', 'SFX_167', 'W6_071', 'S2 PROJ217_54_START', 'W12_K06_bez_nego_in', 'W8_037'],
  ['P3W9-13_G63_hemicycle-dec2025.mp4', 'RIBBON R02_2023-2005', 'SFX_092', 'W7_057 [pan]', 'S3 PCLT_PC04_in', 'S2 V2B_877_70', 'W9_032'],
];
// per-row horizontal jitter so the wall reads organic, not tabular
const OFFSETS = [-40, 60, -110, 20, -70, 90];

export function Wall() {
  return (
    <section id="everything" style={{ position: 'relative', padding: '40px 0', overflow: 'hidden' }}>
      <div style={{ position: 'relative', minHeight: 440, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {/* the wall */}
        <div aria-hidden="true" style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', padding: '30px 0', maskImage: 'radial-gradient(ellipse 82% 90% at 50% 50%, black 30%, transparent 90%)', WebkitMaskImage: 'radial-gradient(ellipse 82% 90% at 50% 50%, black 30%, transparent 90%)' }}>
          {WALL.map((row, i) => (
            <div key={i} style={{ display: 'flex', gap: 44, justifyContent: 'center', whiteSpace: 'nowrap', marginLeft: OFFSETS[i] }}>
              {row.map((f, j) => (
                <span key={f} style={{ fontFamily: 'var(--font-mono)', fontSize: 11.5, letterSpacing: '0.02em', color: 'var(--fg-4)', opacity: (i + j) % 3 === 0 ? 0.7 : 0.45, flex: 'none' }}>{f}</span>
              ))}
            </div>
          ))}
        </div>
        {/* legibility veil behind the words */}
        <div aria-hidden="true" style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse 52% 62% at 50% 50%, rgba(5,5,6,0.92), rgba(5,5,6,0.45) 60%, transparent 80%)' }}></div>

        {/* three one-shot pings sweep the wall as the last line lands */}
        <div aria-hidden="true" style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
          <span className="fnw-ping" style={{ left: '21%', top: '20%' }}><i></i><b></b></span>
          <span className="fnw-ping" style={{ left: '76%', top: '40%' }}><i></i><b></b></span>
          <span className="fnw-ping" style={{ left: '35%', top: '76%' }}><i></i><b></b></span>
        </div>

        {/* the truth */}
        <div style={{ position: 'relative', textAlign: 'center', padding: '0 24px' }}>
          <h2 className="fnw-l1" style={{ margin: 0, fontFamily: 'var(--font-display)', fontSize: 'clamp(38px, 5vw, 64px)', fontWeight: 600, letterSpacing: 'var(--tracking-display)', lineHeight: 1.06, color: 'var(--fg-1)' }}>
            One documentary.
          </h2>
          <p className="fnw-l2" style={{ margin: '10px 0 0', fontFamily: 'var(--font-display)', fontSize: 'clamp(38px, 5vw, 64px)', fontWeight: 600, letterSpacing: 'var(--tracking-display)', lineHeight: 1.06, color: 'rgba(244,245,247,0.26)' }}>
            5,487 clips to place.
          </p>
          <p className="fnw-l3" style={{ margin: '30px 0 0', fontFamily: 'var(--font-mono)', fontSize: 11.5, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--signal-300)' }}>
            That is the part we do
          </p>
        </div>
      </div>
    </section>
  );
}

/* ---------- THE PROJECT: the real timeline, inside a Premiere window ---------- */
type View = { id: string; title: string; text: string; icon: IconName; only: Group[] | null };
const VIEWS: View[] = [
  { id: 'all', title: 'Every clip', text: 'All 5,487, on 30 tracks. This is our first film.', icon: 'layers', only: null },
  { id: 'picture', title: 'The picture', text: 'Footage, AI scenes and graphics, each on its own track.', icon: 'image', only: ['ft', 'ai', 'gfx', 'alt'] },
  { id: 'sound', title: 'The sound', text: 'Voice, interviews, effects and music, mixed and still separate.', icon: 'sliders', only: ['vo', 'ji', 'sfx', 'mus'] },
];
const BINS: [string, string][] = [['Footage', '957'], ['Interviews', '122'], ['AI scenes', '245'], ['Graphics', '1,252'], ['Sound', '1,284'], ['Music', '57'], ['Subtitles', '969']];
const tcOf = (tenths: number) => { const s = Math.floor(tenths / 10); return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`; };

function PremiereWindow({ only, inView }: { only: Group[] | null; inView: boolean }) {
  const settled = useSettled();
  const data = useTimeline();
  const cvRef = useRef<HTMLCanvasElement | null>(null);
  const countRef = useRef<HTMLElement | null>(null);
  const endRef = useRef<HTMLSpanElement | null>(null);
  const t1 = useRef(0); // right edge of the view, tenths of a second
  const onlyRef = useRef(only);
  onlyRef.current = only;
  const started = useRef(false);

  const paint = () => {
    const cv = cvRef.current;
    if (!cv || !data) return;
    drawTimeline(cv, data, 0, t1.current, onlyRef.current);
    if (countRef.current) countRef.current.textContent = clipsBefore(data, t1.current).toLocaleString('en-US');
    if (endRef.current) endRef.current.textContent = tcOf(t1.current);
  };

  // the zoom-out: the view opens on the first 24 seconds and pulls back to the
  // whole 50 minutes while the clip counter runs. Plays once, on first view,
  // and runs to the end even if the section scrolls away meanwhile.
  const raf = useRef(0);
  useEffect(() => {
    if (!data || !inView || started.current) return;
    started.current = true;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) { t1.current = data.end; paint(); return; }
    const from = 240, to = data.end, dur = 5200;
    const t0 = performance.now();
    const tick = (now: number) => {
      const p = Math.min(1, (now - t0) / dur);
      const e = p < 0.5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2; // easeInOutCubic
      t1.current = Math.exp(Math.log(from) + (Math.log(to) - Math.log(from)) * e); // zoom is exponential
      paint();
      if (p < 1) raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, inView]);
  useEffect(() => () => cancelAnimationFrame(raf.current), []);

  // repaint when the highlighted layers change, or the window is resized
  useEffect(() => {
    if (!data) return;
    if (!started.current) t1.current = 240;
    paint();
    const cv = cvRef.current;
    if (!cv) return;
    const ro = new ResizeObserver(paint);
    ro.observe(cv);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, only]);

  const workspaces = ['Assembly', 'Editing', 'Color', 'Effects', 'Audio'];
  return (
    <div data-settled={settled ? 'true' : undefined} style={{ height: '100%', borderRadius: 16, padding: 1, background: 'linear-gradient(160deg, rgba(255,255,255,0.16), rgba(255,255,255,0.04) 35%, rgba(var(--light-soft-rgb),0.22) 75%, rgba(255,255,255,0.05))', boxShadow: '0 40px 100px rgba(0,0,0,0.65), 0 0 110px rgba(var(--light-rgb),0.07)' }}>
      <div style={{ height: '100%', borderRadius: 15, overflow: 'hidden', background: '#101013', display: 'flex', flexDirection: 'column' }}>
        <div style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 8, padding: '7px 14px', borderBottom: '1px solid rgba(0,0,0,0.5)', background: '#1D1D21' }}>
          <span style={{ display: 'flex', gap: 5 }}>
            {['#FF5F57', '#FEBC2E', '#28C840'].map((c) => <i key={c} style={{ width: 9, height: 9, borderRadius: '50%', background: c, opacity: 0.75 }}></i>)}
          </span>
          <PrTile size={16} fs={8} />
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9.5, letterSpacing: '0.05em', color: '#9b9ba3', marginLeft: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>ADOBE PREMIERE PRO · EUROPE HAS A PROBLEM.PRPROJ</span>
        </div>
        {/* Import / Edit / Export + workspaces strip */}
        <div className="crw-nle-workspaces" style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 12, padding: '4px 14px', background: '#19191C', borderBottom: '1px solid rgba(0,0,0,0.5)' }}>
          {['Import', 'Edit', 'Export'].map((m) => (
            <span key={m} style={{ fontSize: 8.5, fontWeight: m === 'Edit' ? 600 : 400, color: m === 'Edit' ? '#DEDEDE' : '#6e6e78', borderBottom: m === 'Edit' ? '1.5px solid #609EFA' : '1.5px solid transparent', paddingBottom: 1 }}>{m}</span>
          ))}
          <span style={{ flex: 1 }}></span>
          {workspaces.map((w) => (
            <span key={w} className="onp-ws" style={{ fontSize: 8, color: w === 'Editing' ? '#DEDEDE' : '#55555e', fontWeight: w === 'Editing' ? 600 : 400 }}>{w}</span>
          ))}
          <span style={{ flex: 1 }}></span>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 7.5, color: '#55555e' }}>1080p · 50 fps</span>
        </div>

        <div className="onp-top">
          {/* the project panel: the bins you get */}
          <div className="onp-pane onp-bins">
            <span className="onp-tab">Project: Europe Has a Problem</span>
            <div className="onp-binlist">
              {BINS.map(([name, n], i) => (
                <div key={name} className="crw-pop" style={{ animationDelay: `${300 + i * 90}ms` }}>
                  <CRWIcon d="folder" size={11} />
                  <span>{name}</span>
                  <i>{n}</i>
                </div>
              ))}
            </div>
          </div>
          {/* the program monitor: the film playing */}
          <div className="onp-pane onp-monitor">
            <span className="onp-tab">Program: MAIN-SEQ</span>
            <div className="onp-screen"><LoopVideo src="/video/film-europe.mp4" poster="/video/film-europe.jpg" /></div>
            <div className="onp-transport">
              <span style={{ color: '#609EFA' }}>00:46:02:35</span>
              <span style={{ flex: 1, textAlign: 'center', letterSpacing: 3, color: '#9b9ba3' }}>◀◀ ◀ ▶ ▶▶</span>
              <span>00:50:23:36</span>
            </div>
          </div>
          {/* the sequence settings, as Premiere lists them */}
          <div className="onp-pane onp-info">
            <span className="onp-tab">Info: MAIN-SEQ</span>
            <dl>
              <div><dt>Frame size</dt><dd>1920 × 1080</dd></div>
              <div><dt>Frame rate</dt><dd>50 fps</dd></div>
              <div><dt>Duration</dt><dd>00:50:23</dd></div>
              <div><dt>Video tracks</dt><dd>15</dd></div>
              <div><dt>Audio tracks</dt><dd>15</dd></div>
              <div><dt>Clips</dt><dd>5,487</dd></div>
            </dl>
          </div>
        </div>

        {/* the sequence: every clip of the film, drawn from the project itself */}
        <div className="onp-seq">
          <div className="onp-ruler">
            <span>00:00</span>
            <span className="onp-count"><b ref={countRef}>0</b> clips</span>
            <span ref={endRef}>00:24</span>
          </div>
          <div className="onp-tracks">
            <span className="onp-vlabel" aria-hidden="true"><i>V15</i><i>V1</i><i>A1</i><i>A15</i></span>
            <canvas ref={cvRef} className="onp-canvas" role="img" aria-label="The timeline of Europe Has a Problem: 5,487 clips on 30 tracks"></canvas>
          </div>
        </div>
      </div>
    </div>
  );
}

export function Project() {
  const [active, setActive] = useState(0);
  const [cycle, setCycle] = useState(0);
  const ref = useRef<HTMLElement | null>(null);
  const [inView, setInView] = useState(false);
  const mobile = useIsMobile();
  const DURATION = 6500;
  useEffect(() => {
    const io = new IntersectionObserver(([e]) => setInView(e.isIntersecting), { threshold: 0.25 });
    if (ref.current) io.observe(ref.current);
    return () => io.disconnect();
  }, []);
  // hold on "Every clip" while the zoom-out plays, then walk the layers
  useEffect(() => {
    if (!inView) return;
    const t = setTimeout(() => { setActive((a) => (a + 1) % VIEWS.length); setCycle((c) => c + 1); }, active === 0 && cycle === 0 ? DURATION + 3500 : DURATION);
    return () => clearTimeout(t);
  }, [active, cycle, inView]);

  return (
    <section id="project" ref={ref} style={{ padding: mobile ? '56px 20px 40px' : '90px 24px 60px' }}>
      <div style={{ maxWidth: 1120, margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 18 }}>
          <span className="crw-glass" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, padding: '8px 16px', borderRadius: 999 }}>
            <PrTile size={24} fs={11} />
            <span style={{ fontSize: 12.5, color: 'var(--fg-2)' }}>A real Adobe Premiere Pro project</span>
          </span>
        </div>
        <SectionHead center title="You get the project. Change anything." sub="This is the timeline of our first film, drawn clip by clip from the project file." />
        <div className="onp-box">
          <div aria-hidden="true" style={{ position: 'absolute', inset: '-90px -120px', background: 'radial-gradient(ellipse 55% 55% at 50% 40%, rgba(var(--light-rgb),0.12), transparent 70%)', pointerEvents: 'none' }}></div>
          <div style={{ position: 'absolute', inset: 0 }}><PremiereWindow only={VIEWS[active].only} inView={inView} /></div>
        </div>
        <div className="crw-how-steps" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, maxWidth: 1040, margin: '20px auto 0' }}>
          {VIEWS.map((t, i) => {
            const on = i === active;
            return (
              <button key={t.id} className="crw-steptab" onClick={() => { setActive(i); setCycle((c) => c + 1); }} style={{ position: 'relative', overflow: 'hidden', padding: '14px 16px', borderRadius: 12, border: '1px solid', borderColor: on ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.08)', background: on ? 'linear-gradient(180deg, rgba(255,255,255,0.07), rgba(255,255,255,0.02))' : 'rgba(255,255,255,0.015)', boxShadow: on ? 'inset 0 1px 0 rgba(255,255,255,0.1)' : 'none' }}>
                {on && inView ? <span key={cycle} className="crw-progress" style={{ animationDuration: `${DURATION}ms` }}></span> : null}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
                  <span style={{ color: on ? 'var(--fg-2)' : 'var(--fg-4)', display: 'inline-flex' }}><CRWIcon d={t.icon} size={14} /></span>
                  <b style={{ fontSize: 14, fontWeight: 600, color: on ? 'var(--fg-1)' : 'var(--fg-3)' }}>{t.title}</b>
                </div>
                <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.55, color: 'var(--fg-3)' }}>{t.text}</p>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ---------- THE CASE FILM: 72 seconds, local file, plays on click ---------- */
// The frame is the site's monitor language: one lit object on the dark scroll.
// The poster is a facade; the film loads and plays only when the visitor asks.
export function CaseFilm() {
  const [playing, setPlaying] = useState(false);
  return (
    <section id="film" aria-labelledby="on-film-h" style={{ padding: '96px 24px' }}>
      <div style={{ maxWidth: 1000, margin: '0 auto' }}>
        <header style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 14, marginBottom: 40 }}>
          <span className="cr-label" style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--signal-400)' }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--signal-400)', boxShadow: '0 0 8px var(--signal-glow)' }}></span>
            The case film
          </span>
          <h2 id="on-film-h" className="crw-h-grad" style={{ margin: 0, fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 'clamp(28px, 3.8vw, 44px)', letterSpacing: 'var(--tracking-display)', lineHeight: 1.1, textWrap: 'balance' }}>
            The whole thing in about a minute.
          </h2>
        </header>

        <div style={{ position: 'relative' }}>
          {/* signal bloom behind the glass: light, not paint */}
          <div aria-hidden="true" style={{ position: 'absolute', inset: '-40px -60px', background: 'radial-gradient(ellipse at 50% 60%, var(--signal-glow), transparent 65%)', filter: 'blur(40px)', opacity: 0.5, pointerEvents: 'none' }}></div>
          <div className="crw-film-frame" style={{ position: 'relative', aspectRatio: '16 / 9', borderRadius: 18, overflow: 'hidden', border: '1px solid var(--line-2)', background: '#000', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.07), 0 30px 80px rgba(0,0,0,0.55)' }}>
            {playing ? (
              <video src={CASE_FILM.src} poster={CASE_FILM.poster} controls autoPlay playsInline style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', display: 'block', background: '#000' }} />
            ) : (
              <button className="crw-film-facade" onClick={() => setPlaying(true)} aria-label="Play the Overnight case film">
                <img src={CASE_FILM.poster} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                <span aria-hidden="true" style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(5,6,9,0.22), transparent 28%, transparent 68%, rgba(5,6,9,0.4))' }}></span>
                {/* play control: the only accent on the glass */}
                <span className="crw-film-play" aria-hidden="true">
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor" style={{ marginLeft: 3 }}><path d="M8 5.5v13l11-6.5z" /></svg>
                </span>
              </button>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 18 }}>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--fg-3)', letterSpacing: '0.06em' }}>{CASE_FILM.length} · WITH SOUND</span>
        </div>
      </div>
    </section>
  );
}

/* ---------- THE FILMS: the two finished documentaries ---------- */
// The films play from our own storage. Safari plays the stream itself; other
// browsers get hls.js, loaded only when a visitor presses play.
function FilmStream({ src, poster, title }: { src: string; poster: string; title: string }) {
  const ref = useRef<HTMLVideoElement | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let hls: { destroy: () => void } | null = null;
    let live = true;
    if (el.canPlayType('application/vnd.apple.mpegurl')) {
      el.src = src;
      void el.play().catch(() => {});
    } else {
      void import('hls.js').then(({ default: Hls }) => {
        if (!live || !Hls.isSupported()) return;
        const h = new Hls({ maxBufferLength: 30 });
        hls = h;
        h.loadSource(src);
        h.attachMedia(el);
        h.on(Hls.Events.MANIFEST_PARSED, () => { void el.play().catch(() => {}); });
      });
    }
    return () => { live = false; hls?.destroy(); };
  }, [src]);
  return <video ref={ref} poster={poster} controls playsInline aria-label={title} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', display: 'block', background: '#000' }} />;
}

// Nothing loads until the visitor presses play: the poster is our own frame.
function FilmCard({ id, title, stream, poster, facts, note }: { id: string; title: string; stream: string; poster: string; facts: [string, string][]; note: string }) {
  const [playing, setPlaying] = useState(false);
  return (
    <article className="onf-card">
      <div className="onf-frame">
        {playing ? (
          <FilmStream src={stream} poster={poster} title={title} />
        ) : (
          <button className="crw-film-facade" onClick={() => setPlaying(true)} aria-label={`Play ${title}`}>
            <img src={poster} alt="" loading="lazy" decoding="async" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
            <span aria-hidden="true" style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(5,6,9,0.25), transparent 30%, transparent 62%, rgba(5,6,9,0.55))' }}></span>
            <span className="crw-film-play" aria-hidden="true">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor" style={{ marginLeft: 3 }}><path d="M8 5.5v13l11-6.5z" /></svg>
            </span>
          </button>
        )}
      </div>
      <div className="onf-meta">
        <div className="onf-title">
          <h3>{title}</h3>
          <a href={`https://www.youtube.com/watch?v=${id}`} target="_blank" rel="noopener noreferrer" className="crw-film-yt">YouTube <CRWIcon d="arrowRight" size={11} /></a>
        </div>
        <div className="onf-facts">
          {facts.map(([n, l]) => <span key={l}><b>{n}</b>{l}</span>)}
        </div>
        <p className="onf-note">{note}</p>
      </div>
    </article>
  );
}

export function Films() {
  return (
    <section id="films" style={{ padding: '70px 24px 90px' }}>
      <div style={{ maxWidth: 1120, margin: '0 auto' }}>
        <SectionHead center label="The films" title="The film this timeline became." sub="Two full documentaries. Both are in Bulgarian." />
        <div className="onf-grid">
          <FilmCard id={FILMS.europe.id} title={FILMS.europe.title} stream={FILMS.europe.stream} poster="/img/poster-europe.jpg"
            facts={[['50', 'minutes'], ['5,487', 'clips'], ['30', 'tracks'], ['7', 'days of work']]}
            note="Why Europe’s economy fell behind. Our first film." />
          <FilmCard id={FILMS.trolls.id} title={FILMS.trolls.title} stream={FILMS.trolls.stream} poster="/img/poster-trolls.jpg"
            facts={[['31', 'minutes'], ['3', 'days of work']]}
            note="Troll farms before an election. Our second film." />
        </div>
      </div>
    </section>
  );
}
